/**
 * Stage 4 — room patch clustering (pure).
 *
 * Flood-fills (column, interval) nodes over the 8-neighborhood: two
 * neighboring intervals connect when their z-overlap covers most of the
 * LARGER interval, so a stairwell/void column (tall interval) or a mezzanine
 * (nested short interval) does not glue two storeys together. Patch heights
 * are trimmed means of the member samples so single bad columns can't skew a
 * room's floor or ceiling.
 *
 * The pairwise overlap rule cannot stop a STAIRCASE, though: each step's
 * interval overlaps the next step's almost fully, so the fill walks the
 * chain from one storey's rooms into the next and the blob's mean floor
 * lands mid-air. A component whose member floors smear wider than the
 * storey tolerance is therefore split into dominant floor bands afterwards —
 * real rooms form tight bands that survive, the sparse stair smear between
 * them falls below the minimum patch area and drops out.
 */

import * as THREE from 'three';
import type { ColumnInterval, RoomPatch } from './types';
import type { FloorDetectionTuning } from './tuning';

/** Trimmed mean: drops `trimFraction` of the sorted samples from each side. */
function trimmedMean(sorted: number[], trimFraction: number): number {
  const trim = Math.floor(sorted.length * trimFraction);
  const from = trim;
  const to = sorted.length - trim;
  let sum = 0;
  for (let i = from; i < to; i++) sum += sorted[i];
  return sum / (to - from);
}

/** p90 − p10 of sorted samples. */
function percentileSpread(sorted: number[]): number {
  const p10 = sorted[Math.floor(sorted.length * 0.1)];
  const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))];
  return p90 - p10;
}

/** z-overlap of two intervals divided by the larger interval height. */
function overlapRatio(a: ColumnInterval, b: ColumnInterval): number {
  const overlap = Math.min(a.ceilingZ, b.ceilingZ) - Math.max(a.floorZ, b.floorZ);
  if (overlap <= 0) return 0;
  const maxHeight = Math.max(a.ceilingZ - a.floorZ, b.ceilingZ - b.floorZ);
  return overlap / maxHeight;
}

interface FloorBand {
  columns: number[];
  intervals: ColumnInterval[];
}

/**
 * Split a connected component into floor-height bands when its floors smear
 * wider than `tolerance` (a staircase chained two storeys together).
 * Repeatedly extracts the band with the most members inside one
 * tolerance-wide floor window (the dominant mode), so each storey's rooms
 * come out as their own band and the stair smear ends up in small leftover
 * bands the area filter discards.
 */
function splitFloorBands(columns: number[], intervals: ColumnInterval[], tolerance: number): FloorBand[] {
  let minFloor = Infinity;
  let maxFloor = -Infinity;
  for (const interval of intervals) {
    minFloor = Math.min(minFloor, interval.floorZ);
    maxFloor = Math.max(maxFloor, interval.floorZ);
  }
  if (maxFloor - minFloor <= tolerance) return [{ columns, intervals }];

  const order = intervals.map((_, i) => i).sort((a, b) => intervals[a].floorZ - intervals[b].floorZ);
  const assigned = new Array<boolean>(intervals.length).fill(false);
  const bands: FloorBand[] = [];
  let remaining = intervals.length;

  while (remaining > 0) {
    const unassigned = order.filter((i) => !assigned[i]);
    // Widest window of floors within `tolerance` of each other (two-pointer
    // over the floor-sorted nodes) = the dominant remaining floor mode.
    let bestFrom = 0;
    let bestTo = 1;
    let lo = 0;
    for (let hi = 0; hi < unassigned.length; hi++) {
      while (intervals[unassigned[hi]].floorZ - intervals[unassigned[lo]].floorZ > tolerance) lo++;
      if (hi + 1 - lo > bestTo - bestFrom) {
        bestFrom = lo;
        bestTo = hi + 1;
      }
    }
    const band: FloorBand = { columns: [], intervals: [] };
    for (let i = bestFrom; i < bestTo; i++) {
      const node = unassigned[i];
      assigned[node] = true;
      band.columns.push(columns[node]);
      band.intervals.push(intervals[node]);
    }
    remaining -= bestTo - bestFrom;
    bands.push(band);
  }
  return bands;
}

const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

/**
 * Cluster column intervals into room patches.
 *
 * @param intervalsByColumn Per-column intervals (index = iy * nx + ix; null/empty = no room there).
 * @param nx,ny             Grid dimensions.
 * @param cellSize          Column size (m).
 * @param originX,originY   World position of the grid's min corner.
 */
export function buildRoomPatches(intervalsByColumn: ReadonlyArray<ColumnInterval[] | null>, nx: number, ny: number, cellSize: number, originX: number, originY: number, tuning: FloorDetectionTuning): RoomPatch[] {
  const minCells = Math.max(1, Math.ceil(tuning.MIN_PATCH_AREA_M2 / (cellSize * cellSize)));

  // Visited flags per (column, intervalIndex) node. Intervals per column are
  // bounded by zRange / MIN_ROOM_HEIGHT, far below 128 for any real scan.
  const visited = new Set<number>();
  const nodeKey = (column: number, intervalIndex: number): number => column * 128 + intervalIndex;

  const patches: RoomPatch[] = [];

  for (let column = 0; column < intervalsByColumn.length; column++) {
    const intervals = intervalsByColumn[column];
    if (!intervals) continue;
    for (let k = 0; k < intervals.length; k++) {
      if (visited.has(nodeKey(column, k))) continue;

      // Flood fill this connected component.
      const memberColumns: number[] = [];
      const memberIntervals: ColumnInterval[] = [];
      const stack: Array<[number, number]> = [[column, k]];
      visited.add(nodeKey(column, k));

      while (stack.length > 0) {
        const top = stack.pop();
        if (!top) break;
        const [col, idx] = top;
        const interval = intervalsByColumn[col]?.[idx];
        if (!interval) continue;
        memberColumns.push(col);
        memberIntervals.push(interval);

        const ix = col % nx;
        const iy = Math.floor(col / nx);
        for (const [dx, dy] of NEIGHBORS) {
          const nix = ix + dx;
          const niy = iy + dy;
          if (nix < 0 || nix >= nx || niy < 0 || niy >= ny) continue;
          const ncol = niy * nx + nix;
          const neighborIntervals = intervalsByColumn[ncol];
          if (!neighborIntervals) continue;
          for (let ni = 0; ni < neighborIntervals.length; ni++) {
            if (visited.has(nodeKey(ncol, ni))) continue;
            if (overlapRatio(interval, neighborIntervals[ni]) >= tuning.INTERVAL_OVERLAP_MIN) {
              visited.add(nodeKey(ncol, ni));
              stack.push([ncol, ni]);
            }
          }
        }
      }

      if (memberColumns.length < minCells) continue;

      // A staircase can chain rooms of two storeys into one component —
      // split such smears into per-storey floor bands (see header comment).
      for (const band of splitFloorBands(memberColumns, memberIntervals, tuning.STOREY_FLOOR_TOLERANCE)) {
        if (band.columns.length < minCells) continue;

        // Degenerate slivers: a band exactly ONE cell thick is a line, not a
        // room — typically half-cut geometry along the selection edge (the
        // rotated selection quad cuts inside the AABB grid) or a wall-top /
        // beam ledge. Real rooms are at least two cells deep in both axes.
        let minIx = Infinity;
        let maxIx = -Infinity;
        let minIy = Infinity;
        let maxIy = -Infinity;
        for (const col of band.columns) {
          const ix = col % nx;
          const iy = Math.floor(col / nx);
          minIx = Math.min(minIx, ix);
          maxIx = Math.max(maxIx, ix);
          minIy = Math.min(minIy, iy);
          maxIy = Math.max(maxIy, iy);
        }
        if (maxIx - minIx === 0 || maxIy - minIy === 0) continue;

        const floorSamples = band.intervals.map((i) => i.floorZ).sort((a, b) => a - b);
        const ceilingSamples = band.intervals.map((i) => i.ceilingZ).sort((a, b) => a - b);
        const floorZ = trimmedMean(floorSamples, tuning.TRIM_FRACTION);
        const ceilingZ = trimmedMean(ceilingSamples, tuning.TRIM_FRACTION);
        const floorZSpread = percentileSpread(floorSamples);
        const ceilingZSpread = percentileSpread(ceilingSamples);

        let floorPointCount = 0;
        let ceilingPointCount = 0;
        for (const interval of band.intervals) {
          floorPointCount += interval.floorCount;
          ceilingPointCount += interval.ceilingCount;
        }

        const bounds = new THREE.Box3();
        const half = cellSize / 2;
        for (const col of band.columns) {
          const cx = originX + ((col % nx) + 0.5) * cellSize;
          const cy = originY + (Math.floor(col / nx) + 0.5) * cellSize;
          bounds.expandByPoint(new THREE.Vector3(cx - half, cy - half, floorZ));
          bounds.expandByPoint(new THREE.Vector3(cx + half, cy + half, ceilingZ));
        }

        patches.push({
          cells: Uint32Array.from(band.columns),
          cellCount: band.columns.length,
          areaM2: band.columns.length * cellSize * cellSize,
          floorZ,
          ceilingZ,
          floorZSpread,
          ceilingZSpread,
          slopedCeiling: ceilingZSpread > tuning.SLOPED_CEILING_SPREAD,
          floorPointCount,
          ceilingPointCount,
          bounds,
        });
      }
    }
  }

  return patches;
}
